/**
 * adyen-connect-api — typed wrappers for the ADMIN Adyen hosted-onboarding ops,
 * through the @v0y4ge/api-client `apiRaw` choke point (paypal-connect-api pattern).
 * REQ-060d — SHIPPED in kit 6686b796: served by the real @v0y4ge/api-mocks
 * (createAdyenConnectDemandHandlers, integrationDetail family). The harness's
 * only remaining shim rewrites onboardingUrl to a hash url for SPA navigation
 * (adyen-preview-seeds.js; retires with REQ-060f).
 *
 * Rules (build brief 2026-07-23): the FE never builds an Adyen URL and never
 * holds platform secrets (API key / merchant account / HMAC are backend-only) —
 * `startAdyenOnboarding({ returnUrl })` returns the backend-minted single-use
 * `onboardingUrl` (request a FRESH one per click, never cache) and the browser
 * navigates to it verbatim. The ONLY source of connected-ness is
 * `getAdyenConnectionStatus()` — return params from the redirect are display-only.
 * `pending` is a REAL intermediate state (onboarded, Adyen KYC/verification in
 * progress, cannot receive money yet) — callers poll until terminal.
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export type AdyenConnectionStatus = "not_connected" | "pending" | "verified";

export interface AdyenConnection {
  status: AdyenConnectionStatus;
  mode: "test" | "live"; // platform-level Adyen environment — display-only
}

/** POST — backend derives the tenant/legal entity; FE sends ONLY returnUrl. */
export const startAdyenOnboarding = (input: { returnUrl: string }) =>
  tag("startAdyenOnboarding", apiRaw<{ onboardingUrl: string }>(
    "/v1/admin/integrations/adyen/onboarding/start",
    { method: "POST", body: JSON.stringify(input) }, 'startAdyenOnboarding'));

export const getAdyenConnectionStatus = () =>
  tag("getAdyenConnectionStatus", apiRaw<AdyenConnection>(
    "/v1/admin/integrations/adyen/connection"));

/** Recent connection-lifecycle events for the "Recent activity" panel (merged
 *  #164: getAdyenActivity). BARE { activity: [{ at, e }] } — derived from the
 *  connection row (onboarding-started, verified); empty until connected. */
export const getAdyenActivity = () =>
  tag("getAdyenActivity", apiRaw<{ activity: { at: string; e: string }[] }>(
    "/v1/admin/integrations/adyen/activity"));

export const disconnectAdyen = () =>
  tag("disconnectAdyen", apiRaw<{ status: "not_connected" }>(
    "/v1/admin/integrations/adyen/connect/disconnect", { method: "POST" }, 'disconnectAdyen'));
